// @ts-nocheck
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export function useFollowActions() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const followMutation = useMutation({
    mutationFn: async (profileId: string) => {
      if (!user?.id) throw new Error('Must be logged in to follow');
      
      const { error } = await supabase
        .from('profile_follows')
        .insert({
          follower_id: user.id,
          followed_id: profileId,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['athlete-search'] });
      queryClient.invalidateQueries({ queryKey: ['popular-athletes'] });
      queryClient.invalidateQueries({ queryKey: ['wall-feed'] });
    },
    onError: (error: Error) => {
      toast.error('Failed to follow', { description: error.message });
    },
  });

  const unfollowMutation = useMutation({
    mutationFn: async (profileId: string) => {
      if (!user?.id) throw new Error('Must be logged in to unfollow');
      
      const { error } = await supabase
        .from('profile_follows')
        .delete()
        .eq('follower_id', user.id)
        .eq('followed_id', profileId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['athlete-search'] });
      queryClient.invalidateQueries({ queryKey: ['popular-athletes'] });
      queryClient.invalidateQueries({ queryKey: ['wall-feed'] });
    },
    onError: (error: Error) => {
      toast.error('Failed to unfollow', { description: error.message });
    },
  });

  const isFollowing = async (profileId: string): Promise<boolean> => {
    if (!user?.id) return false;
    
    const { data } = await supabase
      .from('profile_follows')
      .select('id')
      .eq('follower_id', user.id)
      .eq('followed_id', profileId)
      .maybeSingle();

    return !!data;
  };

  return {
    followUser: (profileId: string) => followMutation.mutate(profileId),
    unfollowUser: (profileId: string) => unfollowMutation.mutate(profileId),
    isFollowing,
    isFollowLoading: followMutation.isPending,
    isUnfollowLoading: unfollowMutation.isPending,
  };
}
